import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { bedService, floorService, roomService, settingsService, tenantService } from '../services/api';
import { Bed, Floor, Room, TenantOnboarding } from '../types';
import EmailLink from '../components/EmailLink';
import PhoneLink from '../components/PhoneLink';
import { formatFloorDisplay } from '../utils/locationLabels';
import './Dashboard.css';
import './TenantOnboarding.css';

function toYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const formatFloorLine = formatFloorDisplay;

function formatSubmitted(o: TenantOnboarding): string {
  const raw = o.createdAt;
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateField(raw?: string): string {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

type OnboardingTab = 'PENDING' | 'REJECTED' | 'DELETED';

const TAB_LABELS: Record<OnboardingTab, string> = {
  PENDING: 'Pending',
  REJECTED: 'Rejected',
  DELETED: 'Deleted',
};

const TenantOnboardingPage: React.FC = () => {
  const { hostel } = useAuth();
  const [activeTab, setActiveTab] = useState<OnboardingTab>('PENDING');
  const [onboardings, setOnboardings] = useState<TenantOnboarding[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState<TenantOnboarding | null>(null);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [beds, setBeds] = useState<Bed[]>([]);
  const [defaultAdvance, setDefaultAdvance] = useState('0');
  const [approving, setApproving] = useState(false);

  const [floorId, setFloorId] = useState<number | ''>('');
  const [roomId, setRoomId] = useState<number | ''>('');
  const [bedId, setBedId] = useState<number | ''>('');
  const [rent, setRent] = useState('');
  const [advance, setAdvance] = useState('0');
  const [dateOfJoining, setDateOfJoining] = useState(toYMD(new Date()));
  const [paymentDate, setPaymentDate] = useState(toYMD(new Date()));

  const joinUrl = useMemo(() => {
    if (!hostel?.hostelCode) return '';
    return `${window.location.origin}/join/${hostel.hostelCode}`;
  }, [hostel?.hostelCode]);

  const loadOnboardings = useCallback(async () => {
    if (!hostel?.id) return;
    try {
      setLoading(true);
      const data = await tenantService.getOnboardingRecords(hostel.id, activeTab);
      setOnboardings(data);
    } catch {
      toast.error(`Failed to load ${TAB_LABELS[activeTab].toLowerCase()} requests`);
    } finally {
      setLoading(false);
    }
  }, [hostel?.id, activeTab]);

  useEffect(() => {
    if (!hostel?.id) return;
    void loadOnboardings();
  }, [hostel?.id, loadOnboardings]);

  useEffect(() => {
    if (!hostel?.id) return;
    floorService.getFloorsByHostel(hostel.id).then((f) => setFloors(f.filter((x) => x.isActive !== false)));
    roomService.getRoomsByHostel(hostel.id).then((r) => setRooms(r.filter((x) => x.isActive !== false)));
    bedService.getBedsByHostel(hostel.id).then((b) => setBeds(b.filter((x) => x.isActive !== false)));
    settingsService
      .getTenantDefaults(hostel.id)
      .then((d) => setDefaultAdvance(d?.defaultNewTenantAdvance != null ? String(d.defaultNewTenantAdvance) : '0'))
      .catch(() => {});
  }, [hostel?.id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return onboardings;
    return onboardings.filter((o) => {
      const name = `${o.firstName} ${o.lastName || ''}`.toLowerCase();
      const hay = [name, o.phoneNumber, o.email, o.workingPlace].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [onboardings, query]);

  useEffect(() => {
    setPage(1);
  }, [query, pageSize, activeTab]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageOnboardingItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const roomsOnFloor = useMemo(() => {
    if (!floorId) return [];
    return rooms.filter((r) => Number(r.floorId) === Number(floorId));
  }, [rooms, floorId]);

  const bedsInRoom = useMemo(() => {
    if (!roomId) return [];
    return beds.filter(
      (b) => Number(b.roomId) === Number(roomId) && String(b.bedStatus).toUpperCase() === 'AVAILABLE',
    );
  }, [beds, roomId]);

  useEffect(() => {
    if (!bedId) return;
    const bed = beds.find((b) => b.id === Number(bedId));
    const room = rooms.find((r) => r.id === Number(roomId));
    if (!bed) return;
    const suggested = bed.rentPerMonth ?? bed.rentPerWeek ?? room?.rentPerBed ?? 0;
    if (suggested) setRent(String(suggested));
  }, [bedId, beds, rooms, roomId]);

  const openApprove = (o: TenantOnboarding) => {
    setSelected(o);
    setFloorId('');
    setRoomId('');
    setBedId('');
    setRent('');
    setAdvance(defaultAdvance);
    setDateOfJoining(toYMD(new Date()));
    setPaymentDate(toYMD(new Date()));
  };

  const copyJoinLink = async () => {
    if (!joinUrl) {
      toast.error('Hostel code not set. Save hostel profile first.');
      return;
    }
    try {
      await navigator.clipboard.writeText(joinUrl);
      toast.success('Registration link copied');
    } catch {
      toast.info(joinUrl);
    }
  };

  const handleApprove = async () => {
    if (!selected?.id) return;
    if (!bedId) {
      toast.error('Select floor, room, and bed');
      return;
    }
    try {
      setApproving(true);
      await tenantService.approveOnboarding(selected.id, {
        kycVerified: Boolean(selected.profilePhoto || selected.idFront),
        bedId: Number(bedId),
        rent: rent || 0,
        advance: advance || 0,
        dateOfJoining,
        paymentDate,
        address: selected.address,
        workingPlace: selected.workingPlace,
        emergencyContactName: selected.emergencyContactName,
        emergencyContactPhoneNumber: selected.emergencyContactPhone,
      });
      toast.success('Tenant approved and checked in');
      setSelected(null);
      await loadOnboardings();
      const bedsRes = await bedService.getBedsByHostel(hostel!.id);
      setBeds(bedsRes.filter((b) => b.isActive !== false));
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Approve failed';
      toast.error(msg);
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async (o: TenantOnboarding) => {
    const reason = window.prompt('Rejection reason (optional):', 'Not approved');
    if (reason === null) return;
    try {
      await tenantService.rejectOnboarding(o.id, reason || 'Not approved');
      toast.success('Request rejected');
      if (selected?.id === o.id) setSelected(null);
      await loadOnboardings();
    } catch {
      toast.error('Reject failed');
    }
  };

  const handleRevertToPending = async (o: TenantOnboarding) => {
    const ok = window.confirm(
      `Move ${o.firstName} ${o.lastName || ''} back to pending? You can approve or reject again.`,
    );
    if (!ok) return;
    try {
      await tenantService.revertOnboardingToPending(o.id);
      toast.success('Moved back to pending');
      if (selected?.id === o.id) setSelected(null);
      await loadOnboardings();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not revert to pending';
      toast.error(msg);
    }
  };

  const handleDelete = async (o: TenantOnboarding) => {
    const ok = window.confirm(`Delete application for ${o.firstName} ${o.lastName || ''}? This cannot be undone.`);
    if (!ok) return;
    try {
      await tenantService.deleteOnboarding(o.id);
      toast.success('Request deleted');
      if (selected?.id === o.id) setSelected(null);
      await loadOnboardings();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Delete failed';
      toast.error(msg);
    }
  };

  if (!hostel) {
    return <div className="onb-loading">No hostel selected</div>;
  }

  return (
    <div className="onb-page">
      <header className="onb-page-header">
        <div>
          <h1 className="onb-title">Onboarding</h1>
          <p className="onb-subtitle">
            <strong>{filtered.length}</strong> {TAB_LABELS[activeTab].toLowerCase()} request
            {filtered.length === 1 ? '' : 's'}
          </p>
        </div>
        {joinUrl ? (
          <div className="onb-join-link">
            <label>Self-registration link</label>
            <div className="onb-join-row">
              <input type="text" readOnly value={joinUrl} onFocus={(e) => e.target.select()} />
              <button type="button" className="btn btn-primary" onClick={() => void copyJoinLink()}>
                Copy
              </button>
            </div>
          </div>
        ) : null}
      </header>

      <div className="onb-toolbar">
        <div className="onb-tabs" role="tablist" aria-label="Onboarding status">
          {(['PENDING', 'REJECTED', 'DELETED'] as OnboardingTab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              className={`onb-tab${activeTab === tab ? ' onb-tab--active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>
        <label className="onb-search">
          <span aria-hidden>🔍</span>
          <input
            type="search"
            placeholder="Search name, phone, email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>

      <section className="onb-table-card">
        {loading ? (
          <p className="onb-empty">Loading {TAB_LABELS[activeTab].toLowerCase()} requests…</p>
        ) : (
          <>
            <div className="onb-table-scroll">
              <table className="onb-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Submitted</th>
                    <th>Source</th>
                    {activeTab === 'REJECTED' ? <th>Reason</th> : null}
                    {activeTab === 'PENDING' ? <th>KYC</th> : null}
                    {activeTab === 'DELETED' ? <th>Deleted</th> : null}
                    {activeTab !== 'DELETED' ? <th>Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {pageOnboardingItems.length === 0 ? (
                    <tr>
                      <td colSpan={activeTab === 'DELETED' ? 6 : 7} className="onb-no-data">
                        No {TAB_LABELS[activeTab].toLowerCase()} onboarding requests.
                      </td>
                    </tr>
                  ) : (
                    pageOnboardingItems.map((o) => (
                      <tr key={o.id}>
                        <td className="onb-col-name">
                          {o.firstName} {o.lastName || ''}
                        </td>
                        <td>
                          <PhoneLink phone={o.phoneNumber} />
                        </td>
                        <td className="onb-col-email">
                          <EmailLink email={o.email} />
                        </td>
                        <td>{formatSubmitted(o)}</td>
                        <td>
                          {o.source === 'SELF' ? (
                            <span className="onb-badge onb-badge--self">Self</span>
                          ) : (
                            <span className="onb-badge">Admin</span>
                          )}
                        </td>
                        {activeTab === 'REJECTED' ? (
                          <td className="onb-col-reason" title={o.rejectionReason || undefined}>
                            {o.rejectionReason || '—'}
                          </td>
                        ) : null}
                        {activeTab === 'PENDING' ? (
                          <td>
                            <span className="onb-kyc-pills">
                              {o.profilePhoto ? <span className="onb-kyc-pill">Photo</span> : null}
                              {o.idFront ? <span className="onb-kyc-pill">ID</span> : null}
                              {!o.profilePhoto && !o.idFront ? '—' : null}
                            </span>
                          </td>
                        ) : null}
                        {activeTab === 'DELETED' ? <td>{formatDateField(o.deletedAt)}</td> : null}
                        {activeTab !== 'DELETED' ? (
                          <td>
                            <div className="onb-row-actions">
                              {activeTab === 'PENDING' ? (
                                <>
                                  <button
                                    type="button"
                                    className="onb-btn onb-btn--approve"
                                    onClick={() => openApprove(o)}
                                  >
                                    Approve
                                  </button>
                                  <button
                                    type="button"
                                    className="onb-btn onb-btn--reject"
                                    onClick={() => void handleReject(o)}
                                  >
                                    Reject
                                  </button>
                                  <button
                                    type="button"
                                    className="onb-btn onb-btn--delete"
                                    onClick={() => void handleDelete(o)}
                                  >
                                    Delete
                                  </button>
                                </>
                              ) : activeTab === 'REJECTED' ? (
                                <>
                                  <button
                                    type="button"
                                    className="onb-btn onb-btn--revert"
                                    onClick={() => void handleRevertToPending(o)}
                                  >
                                    Revert to pending
                                  </button>
                                  <button
                                    type="button"
                                    className="onb-btn onb-btn--delete"
                                    onClick={() => void handleDelete(o)}
                                  >
                                    Delete
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="onb-btn onb-btn--delete"
                                  onClick={() => void handleDelete(o)}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <footer className="onb-table-footer">
              <div className="onb-pagination">
                <button
                  type="button"
                  className="onb-page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ‹ Prev
                </button>
                <span className="onb-page-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="onb-page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next ›
                </button>
              </div>
              <label className="onb-page-size">
                Rows
                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                  <option value={8}>8</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </label>
            </footer>
          </>
        )}
      </section>

      {selected && (
        <div className="onb-modal-overlay" role="presentation" onClick={() => !approving && setSelected(null)}>
          <div className="onb-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="onb-modal-close"
              onClick={() => setSelected(null)}
              disabled={approving}
              aria-label="Close"
            >
              ×
            </button>
            <h2>
              Approve — {selected.firstName} {selected.lastName}
            </h2>
            <p className="onb-modal-meta">
              <PhoneLink phone={selected.phoneNumber} /> · <EmailLink email={selected.email} />
            </p>

            {(selected.profilePhoto || selected.idFront || selected.idBack) && (
              <div className="onb-kyc-grid">
                {selected.profilePhoto ? (
                  <div className="onb-kyc-thumb">
                    <img src={selected.profilePhoto} alt="Profile" />
                    <span>Photo</span>
                  </div>
                ) : null}
                {selected.idFront ? (
                  <div className="onb-kyc-thumb">
                    <img src={selected.idFront} alt="ID front" />
                    <span>ID front</span>
                  </div>
                ) : null}
                {selected.idBack ? (
                  <div className="onb-kyc-thumb">
                    <img src={selected.idBack} alt="ID back" />
                    <span>ID back</span>
                  </div>
                ) : null}
              </div>
            )}

            <div className="onb-approve-form">
              <label>
                Floor *
                <select
                  value={floorId}
                  onChange={(e) => {
                    setFloorId(e.target.value ? Number(e.target.value) : '');
                    setRoomId('');
                    setBedId('');
                  }}
                >
                  <option value="">Select floor</option>
                  {floors.map((f) => (
                    <option key={f.id} value={f.id}>
                      {formatFloorLine(f)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Room *
                <select
                  value={roomId}
                  onChange={(e) => {
                    setRoomId(e.target.value ? Number(e.target.value) : '');
                    setBedId('');
                  }}
                  disabled={!floorId}
                >
                  <option value="">Select room</option>
                  {roomsOnFloor.map((r) => (
                    <option key={r.id} value={r.id}>
                      Room {r.roomNumber}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Bed *
                <select
                  value={bedId}
                  onChange={(e) => setBedId(e.target.value ? Number(e.target.value) : '')}
                  disabled={!roomId}
                >
                  <option value="">Select bed</option>
                  {bedsInRoom.map((b) => (
                    <option key={b.id} value={b.id}>
                      Bed {b.bedNumber}
                    </option>
                  ))}
                </select>
              </label>
              <div className="onb-approve-row">
                <label>
                  Rent (₹)
                  <input type="number" min={0} value={rent} onChange={(e) => setRent(e.target.value)} />
                </label>
                <label>
                  Advance (₹)
                  <input type="number" min={0} value={advance} onChange={(e) => setAdvance(e.target.value)} />
                </label>
              </div>
              <div className="onb-approve-row">
                <label>
                  Joining date *
                  <input type="date" value={dateOfJoining} onChange={(e) => setDateOfJoining(e.target.value)} />
                </label>
                <label>
                  Payment date *
                  <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                </label>
              </div>
              <div className="onb-modal-actions">
                <button type="button" className="btn" onClick={() => setSelected(null)} disabled={approving}>
                  Cancel
                </button>
                <button type="button" className="btn btn-success" disabled={approving} onClick={() => void handleApprove()}>
                  {approving ? 'Approving…' : 'Approve & check in'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantOnboardingPage;
